import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuthContext } from '@shared/auth/AuthContext';
import { smsMoney, smsStatuses, useSmsHistory } from '../smsCredit';

const dateTime = (value: string) =>
  new Date(value).toLocaleString('cs-CZ', {
    timeZone: 'Europe/Prague',
    dateStyle: 'short',
    timeStyle: 'short',
  });
export function SmsHistory({ clientId }: { clientId?: string }) {
  const { t } = useIntlTranslations();
  const { isDemo } = useAuthContext();
  const [filters, setFilters] = useState({ page: 1, status: '', from: '', to: '', search: '' });
  const { data, isLoading, error } = useSmsHistory({ ...filters, clientId });
  const change = (name: string, value: string) =>
    setFilters(current => ({ ...current, [name]: value, page: 1 }));
  if (isDemo) {
    return null;
  }
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">
          {clientId ? t('smsHistory.clientTitle') : t('smsHistory.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('smsHistory.totalCost', {
            amount: smsMoney(data?.chargedHalers ?? 0),
            reserved: data?.reservedHalers
              ? t('smsCredit.reservedSuffix', { amount: smsMoney(data.reservedHalers) })
              : '',
          })}
        </Typography>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} flexWrap="wrap">
        {!clientId && (
          <TextField
            size="small"
            label={t('smsHistory.search')}
            value={filters.search}
            onChange={e => change('search', e.target.value)}
          />
        )}
        <TextField
          size="small"
          select
          label={t('smsHistory.status')}
          value={filters.status}
          onChange={e => change('status', e.target.value)}
          sx={{ minWidth: 155 }}
        >
          <MenuItem value="">{t('smsHistory.allStatuses')}</MenuItem>
          {Object.entries(smsStatuses).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {t(label)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          sx={{ minWidth: 170 }}
          label={t('smsHistory.from')}
          value={filters.from}
          onChange={e => change('from', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          sx={{ minWidth: 170 }}
          label={t('smsHistory.to')}
          value={filters.to}
          onChange={e => change('to', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">{error.message}</Alert>}
      {data && !data.messages.length && (
        <Typography color="text.secondary">{t('smsHistory.empty')}</Typography>
      )}
      {data?.messages.map(message => (
        <Paper key={message.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
              <Typography fontWeight={600}>
                {message.clientName} · {message.phone || t('smsHistory.noPhone')}
              </Typography>
              <Chip
                size="small"
                label={t(smsStatuses[message.status])}
                color={
                  message.status === 'DELIVERED'
                    ? 'success'
                    : ['FAILED', 'UNKNOWN', 'UNDELIVERED'].includes(message.status)
                      ? 'warning'
                      : 'default'
                }
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('smsHistory.visitAndSchedule', {
                appointment: dateTime(message.appointmentAt),
                scheduled: dateTime(message.scheduledAt),
              })}
              {message.sentAt ? t('smsHistory.sentAt', { date: dateTime(message.sentAt) }) : ''}
              {message.deliveredAt
                ? t('smsHistory.deliveredAt', { date: dateTime(message.deliveredAt) })
                : ''}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {message.content}
            </Typography>
            <Typography variant="caption">
              {t('smsHistory.parts', { count: message.parts })} ·{' '}
              {message.chargedHalers
                ? t('smsHistory.charged', { amount: smsMoney(message.chargedHalers) })
                : message.reservedHalers
                  ? t('smsHistory.reserved', { amount: smsMoney(message.reservedHalers) })
                  : message.status === 'PLANNED'
                    ? t('smsHistory.estimated', { amount: smsMoney(message.costHalers) })
                    : t('smsHistory.noCharge')}
            </Typography>
            {message.reason && (
              <Typography variant="body2" color="text.secondary">
                {message.reason}
              </Typography>
            )}
          </Stack>
        </Paper>
      ))}
      {data && data.count > 20 && (
        <Pagination
          count={Math.ceil(data.count / 20)}
          page={filters.page}
          onChange={(_, page) => setFilters(current => ({ ...current, page }))}
        />
      )}
    </Stack>
  );
}
